
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

interface ChartSectionProps {
  data: any[];
}

// Custom label component for bar charts
const CustomLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const formatValue = (val: number) => {
    if (val >= 1000000) {
      return `${(val / 1000000).toFixed(1)}M`;
    } else if (val >= 1000) {
      return `${(val / 1000).toFixed(1)}K`;
    }
    return val.toLocaleString();
  };

  // Calculate font size based on bar height to ensure labels fit
  const fontSize = Math.min(12, Math.max(8, height / 8));
  
  // Only show label if bar is tall enough
  if (height < 20) {
    return null;
  }

  return (
    <text 
      x={x + width / 2} 
      y={y + height / 2} 
      fill="#FFFFFF" 
      textAnchor="middle" 
      fontSize={fontSize}
      fontWeight="600"
      dominantBaseline="middle"
      transform={`rotate(-90, ${x + width / 2}, ${y + height / 2})`}
    >
      {formatValue(value)}
    </text>
  );
};

export default function ChartSection({ data }: ChartSectionProps) {
  if (!data || data.length === 0) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-center h-80">
          <div className="text-center text-gray-500">
            <i className="ri-bar-chart-line text-4xl mb-2"></i>
            <p>No data available for charts</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-center h-80">
          <div className="text-center text-gray-500">
            <i className="ri-pie-chart-line text-4xl mb-2"></i>
            <p>No data available for charts</p>
          </div>
        </div>
      </div>
    );
  }

  // Debug: Log all available columns
  console.log('Available columns:', Object.keys(data[0]));
  console.log('Sample data:', data[0]);

  // Separate employees from company total
  const employees = data.filter(item => !item.is_company_total);
  const companyTotal = data.find(item => item.is_company_total);
  
  // Get all stat columns (excluding id, agent_name, is_company_total)
  const statColumns = Object.keys(data[0]).filter(key => 
    key !== 'id' && key !== 'agent_name' && key !== 'is_company_total'
  );
  
  // Find numeric columns for charts
  const numericColumns = statColumns.filter(col => 
    data.some(item => typeof item[col] === 'number' && item[col] > 0)
  );
  
  // Prepare employee performance chart - show ALL employees
  let employeePerformanceData: any[] = [];
  if (employees.length > 0 && numericColumns.length > 0) {
    // Show ALL employees, not just top performers
    const primaryMetric = numericColumns[0];
    employeePerformanceData = employees
      .filter(emp => {
        const value = emp[primaryMetric];
        // Filter out #DIV/0! and other error values
        return typeof value === 'number' && !isNaN(value) && isFinite(value);
      })
      .sort((a, b) => b[primaryMetric] - a[primaryMetric])
      .map(emp => ({
        name: emp.agent_name || emp.sales_agent || 'Unknown',
        value: emp[primaryMetric] || 0,
        ...Object.fromEntries(
          numericColumns.slice(0, 3).map(col => [
            col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            emp[col] || 0
          ])
        )
      }));
  }
  
  // Prepare BF Average chart - show ALL employees with broker_fee_average
  let bfAverageData: any[] = [];
  if (employees.length > 0) {
    console.log('Looking for broker fee average data in employees...');
    
    // Look for broker_fee_average field directly from the database
    const brokerFeeField = 'broker_fee_average';
    
    if (employees.some(emp => emp[brokerFeeField] !== undefined)) {
      console.log('Found broker_fee_average field in data');
      
      // Show ALL employees with their Broker Fee Average values
      bfAverageData = employees
        .filter(emp => {
          const value = emp[brokerFeeField];
          // Filter out #DIV/0! and other error values
          if (typeof value === 'string' && (value.includes('#DIV/0!') || value.includes('ERROR'))) {
            return false;
          }
          return value !== undefined && value !== null && value !== '';
        })
        .map(emp => {
          let value = 0;
          const rawValue = emp[brokerFeeField];
          
          if (typeof rawValue === 'string') {
            // Parse dollar amount (remove $ and commas)
            value = parseFloat(rawValue.replace(/[$,]/g, '')) || 0;
          } else if (typeof rawValue === 'number') {
            value = rawValue;
          }
          
          // Handle NaN and Infinity
          if (isNaN(value) || !isFinite(value)) {
            value = 0;
          }
          
          return {
            name: emp.sales_agent || emp.agent_name || 'Unknown',
            value: value,
            brokerFeeAverage: value // Add for tooltip
          };
        })
        .sort((a, b) => b.value - a.value); // Sort by broker fee average descending
    } else {
      console.log('broker_fee_average field not found, looking for alternatives...');
      
      // Fallback: look for any column with "broker" and "fee" in the name
      const brokerFeeColumn = Object.keys(data[0]).find(key => {
        const lowerKey = key.toLowerCase();
        return lowerKey.includes('broker') && lowerKey.includes('fee');
      });
      
      console.log('Fallback broker fee column:', brokerFeeColumn);
      
      if (brokerFeeColumn) {
        bfAverageData = employees
          .filter(emp => {
            const value = emp[brokerFeeColumn];
            // Filter out #DIV/0! and other error values
            if (typeof value === 'string' && (value.includes('#DIV/0!') || value.includes('ERROR'))) {
              return false;
            }
            return value !== undefined && value !== null && value !== '';
          })
          .map(emp => {
            let value = 0;
            const rawValue = emp[brokerFeeColumn];
            
            if (typeof rawValue === 'string') {
              value = parseFloat(rawValue.replace(/[$,]/g, '')) || 0;
            } else if (typeof rawValue === 'number') {
              value = rawValue;
            }
            
            // Handle NaN and Infinity
            if (isNaN(value) || !isFinite(value)) {
              value = 0;
            }
            
            return {
              name: emp.sales_agent || emp.agent_name || 'Unknown',
              value: value,
              brokerFeeAverage: value // Add for tooltip
            };
          })
          .sort((a, b) => b.value - a.value);
      }
    }
  }

  // Prepare Total Policies chart - show ALL employees with policies_sold
  let totalPoliciesData: any[] = [];
  if (employees.length > 0) {
    console.log('Looking for policies sold data in employees...');
    
    // Look for policies_sold field directly from the database
    const policiesField = 'policies_sold';
    
    if (employees.some(emp => emp[policiesField] !== undefined)) {
      console.log('Found policies_sold field in data');
      
      // Show ALL employees with their Policies Sold values, EXCLUDING Total row
      totalPoliciesData = employees
        .filter(emp => {
          const value = emp[policiesField];
          // Filter out #DIV/0! and other error values
          if (typeof value === 'string' && (value.includes('#DIV/0!') || value.includes('ERROR'))) {
            return false;
          }
          // EXCLUDE Total row - filter out any agent named "Total"
          if (emp.sales_agent === 'Total' || emp.agent_name === 'Total') {
            return false;
          }
          return value !== undefined && value !== null && value !== '';
        })
        .map(emp => {
          let value = 0;
          const rawValue = emp[policiesField];
          
          if (typeof rawValue === 'string') {
            // Parse number (remove commas)
            value = parseFloat(rawValue.replace(/[,]/g, '')) || 0;
          } else if (typeof rawValue === 'number') {
            value = rawValue;
          }
          
          // Handle NaN and Infinity
          if (isNaN(value) || !isFinite(value)) {
            value = 0;
          }
          
          return {
            name: emp.sales_agent || emp.agent_name || 'Unknown',
            value: value,
            policiesSold: value // Add for tooltip
          };
        })
        .sort((a, b) => b.value - a.value); // Sort by policies sold descending
    }
  }

  console.log('BF Average data:', bfAverageData);
  console.log('Total Policies data:', totalPoliciesData);

  const formatValue = (value: number) => {
    // Handle NaN and Infinity values
    if (isNaN(value) || !isFinite(value)) {
      return '0';
    }
    
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 px-4">
      {/* Agents Policy Sold Chart */}
      {employeePerformanceData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full">
          <div className="flex items-center space-x-2 mb-6">
            <i className="ri-user-line text-blue-500 text-xl"></i>
            <h3 className="text-lg font-semibold text-gray-900">
              Agents Policy Sold Chart
            </h3>
          </div>
          <div className="w-full overflow-x-auto">
            <div className="min-w-[175%] w-[175%] -ml-40">
              <ResponsiveContainer width="100%" height={600}>
                <BarChart data={employeePerformanceData} margin={{ top: 40, right: 40, left: 40, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1F2937' }}
                    angle={-90}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={formatValue}
                    width={120}
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatValue(value), 'Value']}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#3B82F6" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={120}
                  >
                    <LabelList content={CustomLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Broker Fee Average - All Sales Agents Chart */}
      {bfAverageData.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full">
          <div className="flex items-center space-x-2 mb-6">
            <i className="ri-bar-chart-2-line text-orange-500 text-xl"></i>
            <h3 className="text-lg font-semibold text-gray-900">
              Broker Fee Average - All Sales Agents
            </h3>
          </div>
          <div className="w-full overflow-x-auto">
            <div className="min-w-[175%] w-[175%] -ml-40">
              <ResponsiveContainer width="100%" height={600}>
                <BarChart data={bfAverageData} margin={{ top: 40, right: 40, left: 40, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1F2937' }}
                    angle={-90}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${formatValue(value)}`}
                    width={120}
                  />
                  <Tooltip 
                    formatter={(value: number) => [`$${formatValue(value)}`, 'Broker Fee Average']}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#F59E0B" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={120}
                  >
                    <LabelList content={CustomLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex items-center justify-center h-80">
          <div className="text-center text-gray-500">
            <i className="ri-bar-chart-2-line text-4xl mb-2"></i>
            <p>Broker Fee Average data not found</p>
            <p className="text-sm mt-1">Looking for broker_fee_average field in database</p>
            <p className="text-xs mt-2 text-gray-400">Available columns: {Object.keys(data[0]).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Policies - All Sales Agents Chart */}
      {totalPoliciesData.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full">
          <div className="flex items-center space-x-2 mb-6">
            <i className="ri-file-list-3-line text-green-500 text-xl"></i>
            <h3 className="text-lg font-semibold text-gray-900">
              Policies - All Sales Agents
            </h3>
          </div>
          <div className="w-full overflow-x-auto">
            <div className="min-w-[175%] w-[175%] -ml-40">
              <ResponsiveContainer width="100%" height={600}>
                <BarChart data={totalPoliciesData} margin={{ top: 40, right: 40, left: 40, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1F2937' }}
                    angle={-90}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tickMargin={10}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={formatValue}
                    width={120}
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatValue(value), 'Policies Sold']}
                    labelStyle={{ color: '#374151' }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#10B981" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={120}
                  >
                    <LabelList content={CustomLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex items-center justify-center h-80">
          <div className="text-center text-gray-500">
            <i className="ri-file-list-3-line text-4xl mb-2"></i>
            <p>Policies data not found</p>
            <p className="text-sm mt-1">Looking for policies_sold field in database</p>
            <p className="text-xs mt-2 text-gray-400">Available columns: {Object.keys(data[0]).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Fallback if no suitable data for charts */}
      {employeePerformanceData.length === 0 && bfAverageData.length === 0 && totalPoliciesData.length === 0 && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex items-center justify-center h-80">
            <div className="text-center text-gray-500">
              <i className="ri-bar-chart-line text-4xl mb-2"></i>
              <p>Employee charts will appear when numeric data is available</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex items-center justify-center h-80">
            <div className="text-center text-gray-500">
              <i className="ri-pie-chart-line text-4xl mb-2"></i>
              <p>Add employee performance data for visualizations</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
